/**
 * HierarchicalWind — three-tier vegetation wind (trunk / branch / leaf), the
 * production upgrade over the engine's single-sine sway:
 *
 *   · TRUNK — low-frequency directional sway, height²-weighted so roots stay
 *     planted; per-instance phase from the world position hash so a forest
 *     never waves in lockstep.
 *   · BRANCH — mid-frequency bob with per-vertex phase from the object-space
 *     position hash, weighted by radial distance from the trunk axis.
 *   · LEAF — high-frequency flutter along the vertex normal.
 *   · GUST — a traveling noise field over world XZ (scrolled against the
 *     wind direction) modulates all three tiers, so wind arrives in waves.
 *
 * Mask sources: 'heuristic' derives the three weights from geometry (height,
 * radial distance), 'vertexColor' reads the authored R=leaf / G=branch /
 * B=trunk convention (the industry-standard channel mapping).
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, positionLocal, positionGeometry, positionWorld, normalLocal,
  vertexColor, time, sin, fract, mx_noise_float, saturate, smoothstep, normalize, positionPrevious,
} from 'three/tsl';

export interface HierarchicalWindConfig {
  /** Seconds between frames for the motion vectors (default 1/60; drive `handle.frameDt` from the real dt). */
  frameDt?: number;
  /** Wind direction XZ (default [1, 0.3]). */
  direction?: [number, number];
  /** Global strength multiplier (default 1). */
  strength?: number;
  /** Mesh height used to normalize the trunk weight (default 4). */
  meshHeight?: number;
  /**
   * Model-space Y of the trunk BASE (default 0). The trunk weight is measured
   * from here, so a mesh whose pivot is at its centre rather than its base
   * still gets a rigid bottom and a full-amplitude crown. Pass the geometry
   * bounding box's `min.y`.
   */
  meshBaseY?: number;
  /** Trunk sway amplitude in object units (default 0.15). */
  trunkAmplitude?: number;
  /** Trunk sway frequency Hz-ish (default 0.5). */
  trunkFrequency?: number;
  /** Branch bob amplitude (default 0.08). */
  branchAmplitude?: number;
  branchFrequency?: number;
  /** Leaf flutter amplitude (default 0.03). */
  leafAmplitude?: number;
  leafFrequency?: number;
  /** Gust field spatial scale, world units per noise cell (default 25). */
  gustScale?: number;
  /** Gust travel speed (default 2). */
  gustSpeed?: number;
  /** Gust depth 0..1 — how much gusts modulate the base wind (default 0.6). */
  gustStrength?: number;
  /**
   * Fraction of `meshHeight` that is RIGID — the trunk base and roots. Below
   * it nothing sways; the weight ramps from there to full at the crown
   * (default 0.25). Only used by the 'heuristic' mask; the 'vertexColor' mask
   * takes its stiffness from the authored blue channel.
   */
  trunkPivot?: number;
  /** Weight source (default 'heuristic'). */
  maskSource?: 'heuristic' | 'vertexColor';
}

export interface HierarchicalWindHandle {
  /** Previous-frame time offset the motion vectors use (seconds). */
  frameDt: { value: number };
  direction: { value: THREE.Vector2 };
  strength: { value: number };
  gustStrength: { value: number };
}

/** CPU mirror of the gust envelope (exported for tests). noise ∈ [-1,1]. */
export function gustEnvelopeCpu(noise: number, gustStrength: number): number {
  const g = Math.min(Math.max(gustStrength, 0), 1);
  return 1 - g * 0.5 + (noise * 0.5 + 0.5) * g;
}

/**
 * CPU mirror of the trunk weight (exported for tests).
 *
 * `y` is the vertex height in MODEL space — height above the trunk base, not a
 * world coordinate (see the note in applyHierarchicalWind about positionLocal
 * under instancing).
 */
export function trunkWeightCpu(localY: number, meshHeight: number, trunkPivot = 0.25): number {
  const t = Math.min(Math.max(localY / Math.max(meshHeight, 1e-6), 0), 1);
  const p = Math.min(Math.max(trunkPivot, 0), 0.99);
  // smoothstep(p, 1, t), then squared — rigid below the pivot, easing into a
  // full bend at the crown.
  const u = Math.min(Math.max((t - p) / (1 - p), 0), 1);
  const sm = u * u * (3 - 2 * u);
  return sm * sm;
}

export function applyHierarchicalWind(
  material: THREE.Material & { positionNode?: unknown },
  config?: HierarchicalWindConfig,
): HierarchicalWindHandle {
  const dir = config?.direction ?? [1, 0.3];
  const uDir = uniform(vec2(dir[0], dir[1]));
  const uStrength = uniform(config?.strength ?? 1);
  const uGustStrength = uniform(config?.gustStrength ?? 0.6);
  const meshHeight = config?.meshHeight ?? 4;
  const trunkAmp = config?.trunkAmplitude ?? 0.15;
  const trunkFreq = config?.trunkFrequency ?? 0.5;
  const branchAmp = config?.branchAmplitude ?? 0.08;
  const branchFreq = config?.branchFrequency ?? 1.7;
  const leafAmp = config?.leafAmplitude ?? 0.03;
  const leafFreq = config?.leafFrequency ?? 4.3;
  const gustScale = config?.gustScale ?? 25;
  const gustSpeed = config?.gustSpeed ?? 2;
  const useVertexColor = config?.maskSource === 'vertexColor';
  const trunkPivot = Math.min(Math.max(config?.trunkPivot ?? 0.25, 0), 0.99);
  const meshBaseY = config?.meshBaseY ?? 0;
  const uPrevDt = uniform(config?.frameDt ?? 1 / 60); // the previous frame's distance in time (motion vectors)

  material.positionNode = Fn(() => {
    const windDir = normalize(vec3(uDir.x, float(0), uDir.y));

    // Tier weights, measured in MODEL space.
    //
    // WHY positionGeometry AND NOT positionLocal (2026-09-14): three applies
    // `instancedMesh()` / `batch()` to positionLocal BEFORE it builds
    // positionNode (NodeMaterial.setupPosition), so inside here `positionLocal`
    // is the INSTANCE-TRANSFORMED position — a world coordinate. Every one of
    // these weights is a `saturate()` of that divided by a few metres, so for
    // any instanced tree standing away from the origin they all saturated to
    // 1.0 at every vertex: trunk, roots and all. The hierarchy collapsed and
    // the whole model translated rigidly with the wind, which is exactly what
    // it was written to avoid ("height²-weighted so roots stay planted").
    // `positionGeometry` is three's documented pre-transform local position.
    //
    // The trunk weight is rigid below `trunkPivot` and eases to full at the
    // crown, so the base and roots are planted and the motion is carried by
    // the branches and leaves.
    const heightT = saturate(positionGeometry.y.sub(meshBaseY).div(meshHeight));
    const trunkW = useVertexColor
      ? vertexColor().b
      : smoothstep(float(trunkPivot), float(1), heightT).pow(2);
    const branchW = useVertexColor
      ? vertexColor().g
      : saturate(vec2(positionGeometry.x, positionGeometry.z).length().div(meshHeight * 0.5));
    const leafW = useVertexColor ? vertexColor().r : float(1);

    // Gust field traveling against the wind over world XZ.
    const gustUv = positionWorld.xz.div(gustScale)
      .add(vec2(uDir.x, uDir.y).mul(time.mul(gustSpeed).negate().div(gustScale)));
    const gustNoise = mx_noise_float(gustUv);
    const gust = float(1).sub(uGustStrength.mul(0.5))
      .add(gustNoise.mul(0.5).add(0.5).mul(uGustStrength));

    // Per-instance / per-vertex phases (hash of position — no attributes needed).
    const instPhase = fract(positionWorld.x.mul(0.173).add(positionWorld.z.mul(0.311))).mul(6.283);
    // Per-VERTEX phase, also in model space — the same reasoning as the
    // weights. (A phase is read through sin(), so the fract() discontinuity is
    // harmless: 0.999 and 0.001 of a cycle are the same angle.)
    const vertPhase = fract(
      positionGeometry.x.mul(12.9898).add(positionGeometry.y.mul(78.233)).add(positionGeometry.z.mul(37.719)),
    ).mul(6.283);

    // The displacement at a given time — evaluated TWICE: now, and one frame
    // ago for the motion vectors. three's `positionPrevious` defaults to the
    // undisplaced geometry, so a wind offset in positionNode used to read as
    // per-frame VELOCITY equal to the whole sway: the motion-blur / TAA pass
    // smeared every leaf while the camera stood still (2026-09-09, "foliage
    // looks glitchy with motion blur on even when you are stationary").
    // Emitting the true previous position keeps the blur to the real motion.
    const offsetAt = (t: ReturnType<typeof float>) => {
      const trunk = windDir.mul(
        sin(t.mul(trunkFreq * 6.283).add(instPhase)).mul(trunkAmp).mul(trunkW),
      );
      const branch = vec3(0, 1, 0).mul(
        sin(t.mul(branchFreq * 6.283).add(vertPhase)).mul(branchAmp).mul(branchW),
      );
      const leaf = normalLocal.mul(
        sin(t.mul(leafFreq * 6.283).add(vertPhase.mul(2))).mul(leafAmp).mul(leafW),
      );
      return trunk.add(branch).add(leaf).mul(gust).mul(uStrength);
    };
    positionPrevious.assign(positionLocal.add(offsetAt(time.sub(uPrevDt))));
    return positionLocal.add(offsetAt(time));
  })();
  material.needsUpdate = true;

  return {
    direction: uDir as unknown as { value: THREE.Vector2 },
    strength: uStrength as unknown as { value: number },
    gustStrength: uGustStrength as unknown as { value: number },
    frameDt: uPrevDt as unknown as { value: number },
  };
}
